import { type UseMutationResult, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
	createRawRichTextContentStateWithHtmlStringValue,
	ImpersonationBroker,
	type INote,
	type IRawRichTextContentState,
	RichContentReferenceMethod,
} from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';

export interface SaveDealNoteVariables {
	/** Lead/company id — attached as the note's `companies` referenced entity. */
	companyId: string;
	/** HTML string from the rich-text editor; wrapped into `IRawRichTextContentState`. */
	contentHtml: string;
	/**
	 * Existing note id, when updating a note already attached to the deal
	 * (Update-deal flow). When present the save is a `PUT note/{id}`; otherwise
	 * a `POST note` creates a fresh note. Mirrors `NoteViewModel.save`'s
	 * `updateExistingNote` branch (`extViewmodels/ViewModels.ts:1389-1413`).
	 */
	noteId?: string;
}

/**
 * Saves a deal-form `Note` field as a standalone note entity and returns the
 * server-assigned note. The Create/Update Deal request then sends the note's
 * `id` (NOT the HTML) in `values[fieldId]`.
 *
 * This mirrors the legacy two-step deal-note protocol: `CustomFormViewModel.save`
 * calls `f.note.save(f.note.toJs())` then stores `f.note.id` in the form values
 * (`aida/viewModels/form.ts:483-505`). `f.note.save` is the base
 * `NoteViewModel.save`, which POSTs to `note` (or `PUT note/{id}`) —
 * `extViewmodels/ViewModels.ts:1382-1413` — distinct from the call-note
 * `lead/{id}/Note` endpoint used by `useSaveCallNote`.
 *
 * The server expects `content` as `IRawRichTextContentState` (the legacy
 * `<lev-content>` wrapper), so the incoming HTML is run through
 * `createRawRichTextContentStateWithHtmlStringValue` — matching what
 * `editorState.getRawRichTextContent()` returns in the legacy editor.
 */
export function useSaveDealNote(): UseMutationResult<INote, Error, SaveDealNoteVariables> {
	const userSession = useUserSession();

	return useMutation<INote, Error, SaveDealNoteVariables>({
		mutationFn: async ({ companyId, contentHtml, noteId }) => {
			if (!companyId) {
				throw new Error('useSaveDealNote: companyId is required');
			}

			const content: IRawRichTextContentState = createRawRichTextContentStateWithHtmlStringValue(contentHtml ?? '');

			const note: Partial<INote> = {
				content,
				// Legacy nulls `context` for a fresh deal note.
				context: null as unknown as INote['context'],
				visibility: 'all',
				referencedEntities: {
					companies: [{ entity: { id: companyId }, method: RichContentReferenceMethod.Explicit }],
					contacts: [],
					users: [],
				},
			};

			const body = { ...note, ...(noteId ? { id: noteId } : {}), companyId };
			const formData = new FormData();
			formData.append('value', JSON.stringify(body));

			const result = await userSession.webServiceHelper.callAsync<INote>(
				ImpersonationBroker.composeApiUrl({ urlPath: noteId ? `note/${noteId}` : 'note' }),
				noteId ? 'PUT' : 'POST',
				formData
			);

			if (!result || !result.id) {
				// `callAsync` throws on 4xx/5xx; defensive against a 200-with-no-id.
				throw new Error('useSaveDealNote: note save returned no id');
			}
			return result;
		},
		// No MutationCache is configured in this app (see packages/main/CLAUDE.md),
		// so a rejected note save would otherwise fail silently — surface it. The
		// deal create/update awaits this, so the rep needs to know why it aborted.
		onError: error => {
			toast.error(
				error instanceof Error && error.message ? error.message : 'Failed to save the deal note. Please try again.'
			);
		},
	});
}
